<template>
  <div id="cumulative-graph-preview" class="flex flex-col">
    <div class="text-xs text-grey-400 mb-4">
      Number of mastered targets
    </div>
    <div
      class="relative flex justify-center"
      :style="{ width: `${defaultWidth}px` }"
    >
      <div v-if="graphType === 'line'" class="flex">
        <div :style="{ width: `${yAxesWidth}px` }">
          <LineChart
            :key="'y-axes-line-chart'"
            :options="yAxesLineChartOptions"
            :chart-data="yAxesLineChartCollection"
          />
        </div>
        <div
          id="scrollable-chart"
          class="overflow-x-auto scrollbar-lg"
          :style="{ maxWidth: `calc(${defaultWidth}px - ${yAxesWidth}px)` }"
        >
          <LineChart
            :key="'cumulative-line-chart'"
            :style="{ width: `calc(${widthChart}px - ${yAxesWidth}px)` }"
            :options="lineChartOptions"
            :chart-data="lineChartCollection"
          />
        </div>
      </div>
      <div v-if="graphType === 'stacked'" class="flex">
        <div :style="{ width: `${yAxesWidth}px` }">
          <BarChart
            :key="'y-axes-bar-chart'"
            :options="yAxesBarChartOptions"
            :chart-data="yAxesBarChartCollection"
          />
        </div>
        <div
          id="scrollable-chart"
          class="overflow-x-auto scrollbar-lg"
          :style="{ maxWidth: `calc(${defaultWidth}px - ${yAxesWidth}px)` }"
        >
          <BarChart
            :key="'cumulative-stacked-chart'"
            class="cumulative-stacked-chart"
            :style="{ width: `calc(${widthChart}px - ${yAxesWidth}px)` }"
            :options="stackedChartOptions"
            :chart-data="stackedChartCollection"
          />
        </div>
      </div>
    </div>
    <div class="text-xs text-grey-400 self-center mt-2">Date</div>
  </div>
</template>

<script>
import moment from "moment";

import LineChart from "../../charts/LineChart";
import BarChart from "../../charts/BarChart";

export default {
  name: "CumulativeGraphPreview",
  components: { LineChart, BarChart },
  props: ["graphData", "graphType", "progressDisplay"],
  data() {
    return {
      yAxesWidth: 36, // px
      defaultWidth: 928 // px
    };
  },
  computed: {
    widthChart() {
      const labels = this.graphData.labels ? this.graphData.labels.length : 0;
      const scale = labels * 52;
      return scale < this.defaultWidth ? this.defaultWidth : scale;
    },
    //
    lineChartOptions() {
      return {
        legend: { display: false },
        maintainAspectRatio: false,
        tooltips: {
          usePointStyle: true,
          callbacks: {
            title: a => (a.at(0) ? `${a.at(0).value} mastered target(s)` : ""),
            label: a => {
              const display = this.progressDisplay;
              return display === "date"
                ? a.xLabel.join("/")
                : a.xLabel.join(" ");
            }
          }
        },
        layout: { padding: { top: 6 } },
        scales: {
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
                display: false
              },
              gridLines: {
                display: true,
                drawBorder: false
              },
              afterFit: ctx => {
                ctx.width = 0;
              }
            }
          ],
          xAxes: [
            {
              ticks: {
                fontColor: "#707070",
                fontSize: 12
              },
              gridLines: {
                display: true,
                drawBorder: true
              }
            }
          ]
        }
      };
    },
    yAxesLineChartOptions() {
      return {
        legend: { display: false },
        maintainAspectRatio: false,
        tooltips: { enabled: false },
        layout: { padding: { bottom: 34 } },
        scales: {
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
                fontColor: "#707070",
                fontSize: 12
              },
              gridLines: {
                display: false,
                drawBorder: false
              },
              afterFit: ctx => {
                ctx.width = this.yAxesWidth;
              }
            }
          ],
          xAxes: [
            {
              ticks: { display: false },
              gridLines: {
                display: false,
                drawBorder: false
              }
            }
          ]
        }
      };
    },
    //
    lineChartCollection() {
      const { labels = [], datasets = [] } = this.graphData;
      const datacollection = {
        labels: labels.map(date => {
          const display = this.progressDisplay;
          if (display === "date") {
            const arr = date.split("-").reverse();
            return [`${arr.at(0)}/${arr.at(1)}`, arr.at(2)];
          }
          if (display === "month") {
            return [moment(date).format("MMMM"), moment(date).format("YYYY")];
          }
        }),
        datasets: datasets.map(i => {
          return {
            data: i.data,
            backgroundColor: "#9A8AC2",
            borderColor: "#9A8AC2",
            pointRadius: 4,
            fill: false,
            tension: 0.1
          };
        })
      };
      return datacollection;
    },
    yAxesLineChartCollection() {
      const { labels = [], datasets = [] } = this.graphData;
      const datacollection = {
        labels: labels.map(date => ""),
        datasets: datasets.map(i => {
          return {
            data: i.data,
            backgroundColor: "",
            borderColor: "",
            pointRadius: 0,
            fill: false,
            tension: 0.1
          };
        })
      };
      return datacollection;
    },
    //
    stackedChartOptions() {
      const n = this.graphData.labels ? this.graphData.labels.length : 0;
      const barPercentage = n > 19 ? 0.9 : n / 30;
      return {
        legend: { display: false },
        maintainAspectRatio: false,
        tooltips: {
          callbacks: {
            title: (tooltipItem, data) => {
              const datasetsIndex = tooltipItem.at(0).datasetIndex;
              const dataIndex = tooltipItem.at(0).index;
              if (datasetsIndex < 0 || dataIndex < 0) {
                return null;
              }
              const curriculumLabel = data.datasets.at(datasetsIndex).label;
              const valueStacked = data.datasets
                .at(datasetsIndex)
                .data.at(dataIndex);
              return `${curriculumLabel}: ${valueStacked}`;
            },
            label: a => {
              const display = this.progressDisplay;
              return display === "date"
                ? a.xLabel.join("/")
                : a.xLabel.join(" ");
            },
            footer: (tooltipItem, data) => {
              const dataIndex = tooltipItem.at(0).index;
              if (dataIndex < 0) {
                return null;
              }
              let totalStacked = 0;
              for (let i = 0; i < data.datasets.length; i++) {
                totalStacked += data.datasets.at(i).data.at(dataIndex);
              }
              return `Total: ${totalStacked} mastered target(s)`;
            }
          }
        },
        layout: { padding: { top: 6 } },
        scales: {
          yAxes: [
            {
              stacked: true,
              ticks: {
                beginAtZero: true,
                display: false
              },
              gridLines: {
                display: true,
                drawBorder: false
              },
              afterFit: ctx => {
                ctx.width = 0;
              }
            }
          ],
          xAxes: [
            {
              stacked: true,
              ticks: {
                fontColor: "#707070",
                fontSize: 12
              },
              barPercentage,
              gridLines: {
                display: true,
                drawBorder: true
              }
            }
          ]
        }
      };
    },
    yAxesBarChartOptions() {
      const n = this.graphData.labels ? this.graphData.labels.length : 0;
      const barPercentage = n > 19 ? 0.9 : n / 30;
      return {
        legend: { display: false },
        maintainAspectRatio: false,
        tooltips: { enabled: false },
        layout: { padding: { bottom: 34 } },
        scales: {
          yAxes: [
            {
              stacked: true,
              ticks: {
                beginAtZero: true,
                fontColor: "#707070",
                fontSize: 12
              },
              gridLines: {
                display: false,
                drawBorder: false
              },
              afterFit: ctx => {
                ctx.width = this.yAxesWidth;
              }
            }
          ],
          xAxes: [
            {
              stacked: true,
              ticks: { display: false },
              barPercentage,
              gridLines: {
                display: false,
                drawBorder: false
              }
            }
          ]
        }
      };
    },
    //
    stackedChartCollection() {
      const { labels = [], datasets = [] } = this.graphData;
      const datacollection = {
        labels: labels.map(date => {
          const display = this.progressDisplay;
          if (display === "date") {
            const arr = date.split("-").reverse();
            return [`${arr.at(0)}/${arr.at(1)}`, arr.at(2)];
          }
          if (display === "month") {
            return [moment(date).format("MMMM"), moment(date).format("YYYY")];
          }
        }),
        datasets: datasets.map(i => {
          return {
            label: i.label,
            data: i.data,
            backgroundColor:
              i.color +
              Math.round(Math.min(Math.max(0.75), 1) * 255)
                .toString(16)
                .toUpperCase(),
            borderColor: "#E4E4E4",
            hoverBorderColor: "#9A8AC2",
            borderWidth: 0,
            hoverBorderWidth: 2,
            borderRadius: 15
          };
        })
      };
      return datacollection;
    },
    yAxesBarChartCollection() {
      const { labels = [], datasets = [] } = this.graphData;
      const datacollection = {
        labels: labels.map(date => ""),
        datasets: datasets.map(i => {
          return {
            label: i.label,
            data: i.data,
            backgroundColor: "",
            borderColor: "",
            hoverBorderColor: "",
            borderWidth: 0,
            hoverBorderWidth: 0,
            borderRadius: 0
          };
        })
      };
      return datacollection;
    }
  },
  mounted() {
    this.setupAvailableSpace();
    setTimeout(() => {
      const scrollableChart = document.getElementById("scrollable-chart");
      if (scrollableChart) {
        scrollableChart.scrollTo({
          right: 0,
          left: scrollableChart.scrollWidth,
          behavior: "smooth"
        });
      }
    }, 300);
  },
  methods: {
    setupAvailableSpace() {
      const cumulativeGraph = document.getElementById(
        "cumulative-graph-preview"
      );
      if (cumulativeGraph) {
        this.defaultWidth = cumulativeGraph.offsetWidth;
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/clients/cumulativeGraph/GraphPreview.vue